import { Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import { IssueListButton } from "components/Forms/IssueListButton";
import { DynamicField, FacilityInput, Issue, ProgramArea } from "generated-graphql/graphql";
import { transform } from "hooks/transform/transformFacility";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useCallback, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useFacilityInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useFacilityInputValidation";
import { hasCriticalIssues } from "util/forms";

import { FacilityDetailsInput } from "../FacilityProfile";
import { FacilityStateFieldsForm } from "../FacilityStateFieldsForm";
import { useFacility, useStateInformationIssues, useUpdateFacilityProfile } from "../useFacility";
import { STATE_INFORMATION_SECTION_ICON } from "./StateInformationSection";

export function EditStateInformationDialog({ onClose }: { onClose: () => void }) {
  const alerts = useAlerts();
  const { data } = useFacility();
  const facility = data?.facility;
  const [updateFacility, { loading: saving }] = useUpdateFacilityProfile();

  const persistedIssues = useStateInformationIssues();

  const defaultValues = useMemo<FacilityDetailsInput>(
    () => ({
      // Include fields needed by transform and the state fields form
      name: facility?.name ?? "",
      state: facility?.state ?? null,
      streetAddress1: facility?.streetAddress1 ?? null,
      streetAddress2: facility?.streetAddress2 ?? null,
      city: facility?.city ?? null,
      zip: facility?.zip ?? null,
      country: facility?.country ?? null,
      county: facility?.county ?? null,
      latitude: facility?.latitude ?? null,
      longitude: facility?.longitude ?? null,
      phone: facility?.phone ?? null,
      phone24Hour: facility?.phone24Hour ?? null,
      stateFields:
        facility?.stateFields.map((sf) => ({
          ...sf,
          value: sf.value ?? null,
          type: ProgramArea.Epcra,
        })) ?? [],
    }),
    [facility]
  );

  const validator = useFacilityInputValidation({
    facilityId: facility?.id ?? "",
    reportId: undefined,
    pick: ["stateFields"],
  });

  const formValidator = useCallback(
    async (input: FacilityDetailsInput): Promise<Issue[]> => {
      return validator(input);
    },
    [validator]
  );

  const form = useValidatingForm<FacilityDetailsInput>(
    defaultValues,
    persistedIssues,
    formValidator
  );
  const { handleSubmit, formState, issues, watch } = form;
  const stateFields = watch("stateFields");

  const onSubmit = useCallback(
    async (formData: FacilityDetailsInput) => {
      if (!facility) return;

      const input = transform(formData);

      try {
        await updateFacility({
          variables: {
            id: facility.id,
            facility: {
              stateFields: input.stateFields,
            },
          },
        });
        alerts.success("State information saved successfully.");
        onClose();
      } catch (err) {
        alerts.error("Error saving state information.");
        console.info("Error saving state information.", err);
      }
    },
    [facility, updateFacility, alerts, onClose]
  );

  const isCA = facility?.state === "CA";

  return (
    <Dialog
      open
      title={
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h6">Edit</Typography>
          <Typography variant="h6">{STATE_INFORMATION_SECTION_ICON}</Typography>
          <Typography variant="h6">State Information</Typography>
        </Stack>
      }
      onClose={onClose}
      maxWidth="md"
      fullWidth
      primaryActionButtonProps={{
        loading: saving,
        disabled: !formState.isDirty || hasCriticalIssues(issues),
        onClick: () => handleSubmit(onSubmit)(),
      }}
      additionalPrimaryActionElements={[
        <IssueListButton issues={issues} key="issue-list-button" />,
      ]}
    >
      <FormProvider {...form}>
        <form>
          {isCA ? (
            <Typography>
              Because this facility is in California, the California HMBP questions will appear in
              the facility's Tier II report in Chemicals.
            </Typography>
          ) : (
            <FacilityStateFieldsForm
              jurisdictions={
                [facility?.state, facility?.lepc?.id, facility?.fireDepartment?.id].filter(
                  Boolean
                ) as string[]
              }
              context="facility"
              fields={stateFields ?? []}
              namePrefix="stateFields"
            />
          )}
        </form>
      </FormProvider>
    </Dialog>
  );
}
